import { useGetFareCalendarQuery } from '@/src/api/rtkQuery/calendarApi'
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import dayjs from 'dayjs'
import 'dayjs/locale/ar'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useParams, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo } from 'react'
import MobileDateCarousel from './MobileDateCarousel'

dayjs.extend(customParseFormat)

const TOTAL_DAYS = 30 // Total days to show

export const MobileDateCarouselWrapper = () => {
    const { urlQueryObj, isSeoQuery } = useGetSearchParamsObj()
    const { isPerPaxPricingEnabled, jsons } = useSearchResults()
    const partnerId = jsons?.userInfo?.partnerId || ''
    const router = useRouter()
    const routeParams = useParams()
    const locale = (routeParams?.locale as string) || 'en'
    const { formatCurrency } = useFormatCurrencyForSrp()
    // Set dayjs locale based on route locale
    useEffect(() => {
        dayjs.locale(locale)
    }, [locale])

    const selectedDate = urlQueryObj?.depart_date || dayjs().format('DD/MM/YYYY')

    // Calculate date range - 15 days before selected date, 30 days total
    // Calculate based on selectedDate to avoid API call with today's date when selected date is far away
    const dateRange = useMemo(() => {
        const selected = dayjs(selectedDate, 'DD/MM/YYYY')
        const validSelected = selected.isValid() ? selected : dayjs()
        const today = dayjs().startOf('day')

        // Calculate distance between departure date and today
        const daysFromToday = validSelected.diff(today, 'day')

        // If departure date is less than 15 days from today, start from today
        // Otherwise start 15 days before selected date
        let startDate
        if (daysFromToday < 15) {
            startDate = today
        } else {
            const idealStart = validSelected.subtract(15, 'day')
            startDate = idealStart.isBefore(today) ? today : idealStart
        }

        return {
            startDate,
            endDate: startDate.add(TOTAL_DAYS - 1, 'day')
        }
    }, [selectedDate]) // Depend on selectedDate to recalculate when date changes

    // Single API call for all 30 days
    const apiPayload = useMemo(
        () => ({
            queryContext: {
                startDate: dateRange.startDate.format('DD/MM/YYYY'),
                daysCount: 30, // Request all 30 days in one call
                cabin: urlQueryObj?.class?.toUpperCase() || '',
                origin: urlQueryObj?.from || '',
                destination: urlQueryObj?.to || '',
                paxTypeContext: {
                    adultCount: Number(urlQueryObj?.adults) || 1,
                    childCount: Number(urlQueryObj?.childs) || 0,
                    infantCount: Number(urlQueryObj?.infants) || 0
                }
            },
            demandContext: {
                partnerId
            }
        }),
        [dateRange.startDate, urlQueryObj]
    )

    // Make single API call for 30 days
    const { data: fareCalendarData } = useGetFareCalendarQuery({
        payload: apiPayload,
        perPaxPricingRequired: isPerPaxPricingEnabled
    })

    // Generate static array of date items
    const dateItems = useMemo(() => {
        const items = []
        const fareCalendar = fareCalendarData?.fareCalendar || {}

        // Generate all 30 date items
        for (let i = 0; i < TOTAL_DAYS; i++) {
            const date = dateRange.startDate.add(i, 'day')
            // RTK query transform uses YYYY-MM-DD lookup keys
            const dateKey = date.format('YYYY-MM-DD')
            const fare = fareCalendar[dateKey]
            const price = fare?.amount
            // Clone date and set locale for this specific format
            const localizedDate = date.locale(locale)

            items.push({
                date: localizedDate.format('D MMM'),
                day: localizedDate.format('ddd'),
                price: price !== undefined && price !== null && price > 0 ? formatCurrency(price) : '-',
                isSelected: date.format('DD/MM/YYYY') === selectedDate,
                isLowest: fare?.fareCategory === 'CHEAPEST'
            })
        }

        return items
    }, [dateRange.startDate, fareCalendarData, formatCurrency, selectedDate, locale])

    // Calculate current selected index
    const selectedIndex = useMemo(() => {
        const selected = dayjs(selectedDate, 'DD/MM/YYYY')
        if (selected.isValid()) {
            const diff = selected.diff(dateRange.startDate, 'day')
            return diff >= 0 && diff < TOTAL_DAYS ? diff : -1
        }
        return -1
    }, [selectedDate, dateRange.startDate])

    // Handle date selection
    const handleSelect = useCallback(
        (index: number) => {
            const selectedDay = dateRange.startDate.add(index, 'day')
            const currentParams = new URLSearchParams(urlQueryObj)
            currentParams.set('depart_date', selectedDay.format('DD/MM/YYYY'))
            const newUrl = isSeoQuery
                ? `/${locale}/flights/results?${currentParams.toString()}`
                : `${window.location.pathname}?${currentParams.toString()}`
            router.push(newUrl)
        },
        [dateRange.startDate, urlQueryObj, router]
    )

    return <MobileDateCarousel dates={dateItems} onSelect={handleSelect} selectedIndex={selectedIndex} />
}

export default MobileDateCarouselWrapper
